import { NavLink, useLocation } from "react-router";
import {
    Sidebar,
    SidebarContent,
    SidebarGroup,
    SidebarGroupContent,
    SidebarGroupLabel,
    SidebarMenu,
    SidebarMenuButton,
    SidebarMenuItem,
    SidebarMenuSub,
    SidebarMenuSubItem,
} from "@/components/ui/sidebar";
import React from "react";
import { FeatureFlag } from "@/constants";
import AccessGuard from "@/components/access-guard";
import { Collapsible } from "@radix-ui/react-collapsible";
import {
    CollapsibleContent,
    CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { ChevronRight } from "@/assets";
import { useUser } from "@/contexts/user";
import { Skeleton } from "@/components/ui/skeleton";
import { useDeviceGroupDetail } from "@/api/device-group-http-functions";
import { useIsStaff } from "@/hooks/useIsStaff";
import { useIsCustomer } from "@/hooks/useIsCustomer";

export const SettingsSidebar = ({
    ref,
    ...props
}: React.ComponentProps<"div"> & {
    side?: "left" | "right";
    variant?: "sidebar" | "floating" | "inset";
    collapsible?: "offcanvas" | "icon" | "none";
}) => {
    const { user, isLoading: isLoadingUser } = useUser();
    const isStaff = useIsStaff();
    const isCustomer = useIsCustomer();
    const { data: organisation, isLoading: isLoadingOrganisation } =
        useDeviceGroupDetail(user?.organisationId as string, {
            query: { enabled: !!user?.organisationId },
        });
    const location = useLocation();
    return (
        <Sidebar {...props} ref={ref}>
            {props.children}
            <SidebarContent>
                <SidebarGroup>
                    <SidebarGroupLabel>User</SidebarGroupLabel>
                    <SidebarGroupContent>
                        <SidebarMenu>
                            <SidebarMenuItem>
                                <SidebarMenuButton asChild>
                                    <NavLink
                                        className={"text-default-color"}
                                        to={`/settings/account`}
                                        end
                                    >
                                        <span>My account</span>
                                    </NavLink>
                                </SidebarMenuButton>
                            </SidebarMenuItem>
                            <AccessGuard
                                permissions={["notification_preferences_read"]}
                                unauthorizedBehaviour={"hide"}
                            >
                                <SidebarMenuItem>
                                    <SidebarMenuButton asChild>
                                        <NavLink
                                            className={"text-default-color"}
                                            to={`/settings/notification-preferences`}
                                            end
                                        >
                                            <span>
                                                Notification preferences
                                            </span>
                                        </NavLink>
                                    </SidebarMenuButton>
                                </SidebarMenuItem>
                            </AccessGuard>
                        </SidebarMenu>
                    </SidebarGroupContent>
                </SidebarGroup>
                <SidebarGroup>
                    <SidebarGroupLabel>
                        {isLoadingUser || isLoadingOrganisation ? (
                            <Skeleton className="h-6 w-40" />
                        ) : (
                            (organisation?.deviceGroupName ?? "Organisation")
                        )}
                    </SidebarGroupLabel>
                    <SidebarGroupContent>
                        <SidebarMenu>
                            <SidebarMenuItem>
                                <SidebarMenuButton asChild>
                                    <NavLink
                                        className={"text-default-color"}
                                        to={`/settings/organisation`}
                                    >
                                        <span>Organisation preferences</span>
                                    </NavLink>
                                </SidebarMenuButton>
                            </SidebarMenuItem>
                            <AccessGuard
                                permissions={[
                                    "system_user_account_manage",
                                    "enterprise_user_account_read",
                                    "user_tasks_read",
                                    "system_device_provisioning",
                                ]}
                                permissionsSelector="any"
                                unauthorizedBehaviour="hide"
                            >
                                <Collapsible
                                    className="group/collapsible"
                                    defaultOpen={[
                                        "users",
                                        "roles",
                                        "user-action-log",
                                        "payment-log",
                                    ].some((path) =>
                                        location.pathname.includes(`/${path}`),
                                    )}
                                >
                                    <SidebarMenuItem>
                                        <CollapsibleTrigger className="relative w-full">
                                            <div className="absolute -left-6 flex h-10 items-center">
                                                <ChevronRight className="transition-transform group-data-[state=open]/collapsible:rotate-90" />
                                            </div>
                                            <SidebarMenuButton asChild>
                                                <span>User accounts</span>
                                            </SidebarMenuButton>
                                        </CollapsibleTrigger>
                                        <CollapsibleContent>
                                            <SidebarMenuSub>
                                                <AccessGuard
                                                    permissions={[
                                                        "system_user_account_manage",
                                                        "enterprise_user_account_read",
                                                    ]}
                                                    permissionsSelector="any"
                                                    unauthorizedBehaviour={
                                                        "hide"
                                                    }
                                                >
                                                    <SidebarMenuSubItem>
                                                        <SidebarMenuButton
                                                            asChild
                                                        >
                                                            <NavLink
                                                                className="text-default-color"
                                                                to={`/settings/users`}
                                                            >
                                                                <span>
                                                                    Users
                                                                </span>
                                                            </NavLink>
                                                        </SidebarMenuButton>
                                                    </SidebarMenuSubItem>
                                                </AccessGuard>
                                                <AccessGuard
                                                    overrideAndRestrict={
                                                        !isStaff
                                                    }
                                                    unauthorizedBehaviour="hide"
                                                >
                                                    <SidebarMenuSubItem>
                                                        <SidebarMenuButton
                                                            asChild
                                                        >
                                                            <NavLink
                                                                className="text-default-color"
                                                                to={`/settings/roles`}
                                                            >
                                                                <span>
                                                                    Roles
                                                                </span>
                                                            </NavLink>
                                                        </SidebarMenuButton>
                                                    </SidebarMenuSubItem>
                                                </AccessGuard>
                                                <AccessGuard
                                                    permissions={[
                                                        "user_tasks_read",
                                                    ]}
                                                    unauthorizedBehaviour={
                                                        "hide"
                                                    }
                                                >
                                                    <SidebarMenuSubItem>
                                                        <SidebarMenuButton
                                                            asChild
                                                        >
                                                            <NavLink
                                                                className="text-default-color"
                                                                to={`/settings/user-action-log`}
                                                            >
                                                                <span>
                                                                    User action
                                                                    log
                                                                </span>
                                                            </NavLink>
                                                        </SidebarMenuButton>
                                                    </SidebarMenuSubItem>
                                                </AccessGuard>
                                                <AccessGuard
                                                    permissions={[
                                                        "system_device_provisioning",
                                                    ]}
                                                    featureFlags={[
                                                        FeatureFlag.PAYMENT_LOGS_FEATURE_FLAG,
                                                    ]}
                                                    unauthorizedBehaviour={
                                                        "hide"
                                                    }
                                                >
                                                    <SidebarMenuSubItem>
                                                        <SidebarMenuButton
                                                            asChild
                                                        >
                                                            <NavLink
                                                                className="text-default-color"
                                                                to={`/settings/payment-log`}
                                                            >
                                                                <span>
                                                                    Payment log
                                                                </span>
                                                            </NavLink>
                                                        </SidebarMenuButton>
                                                    </SidebarMenuSubItem>
                                                </AccessGuard>
                                            </SidebarMenuSub>
                                        </CollapsibleContent>
                                    </SidebarMenuItem>
                                </Collapsible>
                            </AccessGuard>
                            <AccessGuard
                                permissions={[
                                    "rfid_tags_read",
                                    "rfid_tags_manage",
                                    "system_rfid_tags_manage",
                                ]}
                                permissionsSelector="any"
                                unauthorizedBehaviour={"hide"}
                            >
                                <SidebarMenuItem>
                                    <SidebarMenuButton asChild>
                                        <NavLink
                                            data-tour-step="rfid-management-tab"
                                            className={"text-default-color"}
                                            to={`/settings/rfid`}
                                            end
                                        >
                                            <span>RFID tags</span>
                                        </NavLink>
                                    </SidebarMenuButton>
                                </SidebarMenuItem>
                            </AccessGuard>
                            <AccessGuard
                                permissions={["device_pricing_read"]}
                                featureFlags={[FeatureFlag.EM_25_OFFERS]}
                                unauthorizedBehaviour={"hide"}
                            >
                                <SidebarMenuItem>
                                    <SidebarMenuButton asChild>
                                        <NavLink
                                            className={"text-default-color"}
                                            to={`/settings/offers`}
                                            end
                                        >
                                            <span>Offers</span>
                                        </NavLink>
                                    </SidebarMenuButton>
                                </SidebarMenuItem>
                            </AccessGuard>
                            <AccessGuard
                                permissions={[
                                    "device_pricing_read",
                                    "device_pricing_manage",
                                ]}
                                permissionsSelector="any"
                                featureFlags={[FeatureFlag.TARIFF_FEATURE_FLAG]}
                                unauthorizedBehaviour={"hide"}
                            >
                                <SidebarMenuItem>
                                    <SidebarMenuButton asChild>
                                        <NavLink
                                            className={"text-default-color"}
                                            to={`/settings/tariffs`}
                                            end
                                        >
                                            <span>Tariffs</span>
                                        </NavLink>
                                    </SidebarMenuButton>
                                </SidebarMenuItem>
                            </AccessGuard>
                            <AccessGuard
                                overrideAndRestrict={!isStaff && !isCustomer}
                                unauthorizedBehaviour="hide"
                            >
                                <SidebarMenuItem>
                                    <SidebarMenuButton asChild>
                                        <NavLink
                                            className={"text-default-color"}
                                            to={`/settings/device-groups`}
                                        >
                                            <span>Device groups</span>
                                        </NavLink>
                                    </SidebarMenuButton>
                                </SidebarMenuItem>
                            </AccessGuard>
                            <AccessGuard
                                permissions={["system_device_catalogue_update"]}
                                featureFlags={[
                                    FeatureFlag.IL_3915_DEVICE_CATALOGUE,
                                ]}
                                unauthorizedBehaviour="hide"
                            >
                                <SidebarMenuItem>
                                    <SidebarMenuButton asChild>
                                        <NavLink
                                            className={"text-default-color"}
                                            to={`/settings/device-catalogue`}
                                            end
                                        >
                                            <span>Device catalogue</span>
                                        </NavLink>
                                    </SidebarMenuButton>
                                </SidebarMenuItem>
                            </AccessGuard>
                        </SidebarMenu>
                    </SidebarGroupContent>
                </SidebarGroup>
            </SidebarContent>
        </Sidebar>
    );
};
